import { NetworkStatus } from '@apollo/client'
import { useDownload } from '@web/react-common'
import { useDebounceFn } from 'ahooks'
import { Avatar, Button, Input, List, Spin } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserHoverCard } from '@/components'
import { useGetUserListQuery } from '@/generated/graphql'

import s from './users-list.module.less'

export type UserListFetchType =
  | 'following'
  | 'followed'
  | 'groupMembers'
  | 'groupAdmins'
  | 'likedUsers'
  | 'readUsers'
  | 'allUsers'
  | 'sharedUsers'
  | 'feltUsefulUsers'
  | 'eventParticipants'
  | 'eventInterestedUsers'
  | 'yesterdayVisitors'
  | 'userPageVisitors'
export interface UsersListProps {
  fetchCount?: number
  showSearch?: boolean
  showLoadmore?: boolean
  showIndex?: boolean
  onClickUser: (u: { userId: number; email: string }) => void
  type?: UserListFetchType
  args?: { hasOnboarding?: boolean; isOnline?: boolean; includeDeleted?: boolean }
  userId?: number
  groupId?: number
  postId?: number
  eventId?: number
  extra?: (userId: number) => React.ReactNode
  paginationInput?: { sortBy: string; useFirst: boolean }
  pinTop?: boolean
}

export const UsersList: React.FC<UsersListProps> = ({
  fetchCount = 8,
  showSearch = true,
  showLoadmore = true,
  showIndex = false,
  onClickUser,
  type = 'following',
  args = {},
  userId = 0,
  groupId = 0,
  postId = 0,
  eventId = 0,
  paginationInput,
  extra,
  pinTop = true,
}) => {
  const { t } = useTranslation()
  const [search, setSearchStr] = React.useState('')
  const { run: setSearch } = useDebounceFn(setSearchStr, { wait: 300 })
  const cursor = paginationInput?.useFirst || search ? 'first' : 'last'
  const pagination = (() => {
    const sortBy = paginationInput?.sortBy || (search ? 'name' : undefined)
    return {
      sortBy,
      [cursor]: fetchCount,
    }
  })()
  const { data, error, fetchMore, networkStatus } = useGetUserListQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      userId,
      groupId,
      postId,
      eventId,
      pagination,
      ...(type === 'following' && { withUser: true, withFollowingUsers: true }),
      ...(type === 'followed' && { withUser: true, withFollowedUsers: true }),
      ...(type === 'yesterdayVisitors' && { withYesterdayVisitors: true, withUser: true }),
      ...(type === 'userPageVisitors' && { withUserPageVisitors: true, withUser: true }),
      ...(type === 'groupMembers' && { withGroup: true, withGroupMembers: true }),
      ...(type === 'groupAdmins' && { withGroup: true, withGroupAdmins: true }),
      ...(type === 'likedUsers' && { withPost: true, withPostLikedUsers: true }),
      ...(type === 'sharedUsers' && { withPost: true, withPostSharedUsers: true }),
      ...(type === 'readUsers' && { withPost: true, withPostReadUsers: true }),
      ...(type === 'feltUsefulUsers' && { withPost: true, withPostFeltUsefulUsers: true }),
      ...(type === 'eventParticipants' && { withEvent: true, withEventParticipants: true }),
      ...(type === 'eventInterestedUsers' && { withEvent: true, withEventInterestedUsers: true }),
      ...(type === 'allUsers' && { withAllUsers: true }),
      filter: { ...args, ...(!!search && { search }) },
    },
  })

  if (error) {
    return null
  }

  const onLoadmore = () => {
    fetchMore({
      variables: {
        pagination: {
          ...pagination,
          [cursor === 'first' ? 'after' : 'before']: cursor === 'first' ? pageInfo.endCursor : pageInfo.startCursor,
        },
      },
    })
  }

  const { users, pageInfo } = (() => {
    if (!data) {
      return {
        users: [],
        pageInfo: {
          startCursor: null,
          endCursor: null,
          hasNextPage: null,
          hasPreviousPage: null,
        },
      }
    }
    switch (type) {
      case 'groupAdmins':
        return { users: data.group!.admins!.edges.slice(), pageInfo: data.group!.admins!.pageInfo }
      case 'groupMembers':
        return { users: data.group!.followers!.edges.slice(), pageInfo: data.group!.followers!.pageInfo }
      case 'followed':
        return { users: data.user!.followedUsers!.edges.slice(), pageInfo: data.user!.followedUsers!.pageInfo }
      case 'following':
        return { users: data.user!.followingUsers!.edges.slice(), pageInfo: data.user!.followingUsers!.pageInfo }
      case 'likedUsers':
        return { users: data.post!.likedUsers!.edges.slice(), pageInfo: data.post!.likedUsers!.pageInfo }
      case 'readUsers':
        return { users: data.post!.readUsers!.edges.slice(), pageInfo: data.post!.readUsers!.pageInfo }
      case 'sharedUsers':
        return { users: data.post!.sharedUsers!.edges.slice(), pageInfo: data.post!.sharedUsers!.pageInfo }
      case 'feltUsefulUsers':
        return { users: data.post!.feltUsefulUsers!.edges.slice(), pageInfo: data.post!.feltUsefulUsers!.pageInfo }
      case 'eventParticipants':
        return {
          users: data.event!.participants!.edges.slice(),
          pageInfo: data.event!.participants!.pageInfo,
        }
      case 'eventInterestedUsers':
        return {
          users: data.event!.interestedUsers!.edges.slice(),
          pageInfo: data.event!.interestedUsers!.pageInfo,
        }
      case 'allUsers':
        return { users: data.users!.edges.slice(), pageInfo: data.users!.pageInfo }
      case 'yesterdayVisitors':
        return {
          users: data.user!.yesterdayVisitors!.edges.slice(),
          pageInfo: data.user!.yesterdayVisitors!.pageInfo,
        }
      case 'userPageVisitors':
        return {
          users: data.user!.viewedUsers!.edges.slice(),
          pageInfo: data.user!.viewedUsers!.pageInfo,
        }
    }
  })()

  if (cursor === 'last') {
    users.reverse()
  }

  const userCount = type === 'allUsers' ? data?.users!.totalCount : 0

  const participates = (
    data && type === 'eventParticipants' ? data.event!.participants!.edges.slice().reverse() : []
  ).map(({ node }) => node)
  const file = [['姓名', '部门', '邮箱']].concat(
    participates ? participates.map((p) => [p.name, p.departments[0]?.name || '', p.email]) : [['', '', '']]
  )
  const eventName = data && type === 'eventParticipants' ? data.event!.topic : ''

  const header = showSearch && (
    <div className={s.header}>
      <Input.Search
        className={s.search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        onSearch={(s) => setSearch(s)}
        placeholder={t('search.searchUser')}
      />
      {type === 'allUsers' && userCount}
      {type === 'eventParticipants' && (
        <Button type='link' onClick={() => useDownload(file, `${eventName}-报名统计`)}>
          下载名单
        </Button>
      )}
    </div>
  )

  const footer = () => {
    if (networkStatus === NetworkStatus.fetchMore) {
      return <Spin />
    }
    if (showLoadmore && (cursor === 'first' ? pageInfo.hasNextPage : pageInfo.hasPreviousPage)) {
      return (
        <Button shape='round' onClick={onLoadmore}>
          {t('groups.grouplist.loadmore')}
        </Button>
      )
    }
  }

  const style = pinTop ? { maxHeight: 'calc(100vh * 0.85)', overflow: 'auto' } : undefined

  return (
    <List
      style={style}
      className={s.list}
      loading={networkStatus === NetworkStatus.loading}
      header={header}
      footer={footer()}
      dataSource={users}
      split={false}
      renderItem={({ node: u }, index) => (
        <List.Item
          key={u.userId}
          className={s.listItem}
          onClick={() => onClickUser(u)}
          extra={extra && extra(u.userId)}
        >
          <List.Item.Meta
            avatar={<Avatar src={u.avatarUrl} />}
            title={
              <UserHoverCard email={u.email}>
                <a>
                  {showIndex && !search && `${index + 1}. `}
                  {u.name}
                </a>
              </UserHoverCard>
            }
            description={u.departments.map((d) => d.name).join(' - ')}
          />
        </List.Item>
      )}
    />
  )
}
